import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaCheck,
  FaSave,
  FaSearch,
  FaTimes,
  FaUserMinus,
  FaUserPlus,
  FaUsers,
} from "react-icons/fa";
import { toast } from "react-toastify";

import UserDrawer from "./UserDrawer";

import { getAllAdherentsListWithGroup } from "../../../../../services/adherentServices";

import {
  assignAdherentToUser,
  removeAdherentFromUser,
  getAdherentsByUser,
} from "../../../../../services/userServices";

const API_BASE = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
const MAX_SUGGESTIONS = 60;

function toAbsolute(url) {
  if (!url) return "";

  const value = String(url).trim();

  if (
    /^https?:\/\//i.test(value) ||
    value.startsWith("data:") ||
    value.startsWith("blob:")
  ) {
    return value;
  }

  if (value.startsWith("/uploads")) {
    return `${API_BASE}${value}`;
  }

  return value;
}

function fallbackPhoto(genre) {
  return genre === "Féminin"
    ? "/img/judokate_avatar.png"
    : "/img/judoka_avatar.png";
}

function getAdherentPhoto(adherent) {
  const photo =
    adherent?.photo_url ||
    adherent?.photoURL ||
    adherent?.photo ||
    adherent?.image_url ||
    "";

  if (!photo || photo === "/img/default_avatar.png") {
    return fallbackPhoto(adherent?.genre);
  }

  return toAbsolute(photo);
}

function getAdherentName(adherent) {
  const nom = String(adherent?.nom || "").trim().toUpperCase();
  const prenom = String(adherent?.prenom || "").trim();

  return `${nom} ${prenom}`.trim() || "Adhérent";
}

function formatGroups(adherent) {
  if (Array.isArray(adherent?.Groups) && adherent.Groups.length) {
    return adherent.Groups.map((group) => group?.nom).filter(Boolean).join(", ");
  }

  if (Array.isArray(adherent?.groups) && adherent.groups.length) {
    return adherent.groups
      .map((group) => group?.nom || group?.name)
      .filter(Boolean)
      .join(", ");
  }

  return "Sans groupe";
}

function getAdherentStatusClasses(status) {
  switch (String(status || "").trim()) {
    case "Licencié":
      return "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20";
    case "Inscrit":
      return "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20";
    case "Cours d'essai":
    case "Cours d’essai":
      return "bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/20";
    default:
      return "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700";
  }
}

function AdherentSuggestionRow({ adherent, active, onAdd }) {
  return (
    <button
      type="button"
      onClick={() => onAdd(adherent.id)}
      className={`flex w-full items-center gap-3 px-3 py-2 text-left transition ${
        active
          ? "bg-indigo-50 dark:bg-indigo-500/10"
          : "hover:bg-slate-50 dark:hover:bg-slate-900"
      }`}
    >
      <img
        src={getAdherentPhoto(adherent)}
        alt={getAdherentName(adherent)}
        className="h-10 w-10 rounded-2xl object-cover ring-1 ring-slate-200 dark:ring-slate-700"
        onError={(event) => {
          event.currentTarget.src = fallbackPhoto(adherent?.genre);
        }}
      />

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-bold text-slate-900 dark:text-white">
          {getAdherentName(adherent)}
        </p>

        <p className="truncate text-xs text-slate-500 dark:text-slate-400">
          {formatGroups(adherent)}
        </p>
      </div>

      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-1 text-xs font-bold text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20">
        <FaUserPlus />
        Ajouter
      </span>
    </button>
  );
}

function SelectedAdherentPill({ adherent, onRemove }) {
  return (
    <div className="group flex items-center gap-2 rounded-full border border-slate-200 bg-white px-2 py-1 shadow-sm dark:border-slate-700 dark:bg-slate-950">
      <img
        src={getAdherentPhoto(adherent)}
        alt={getAdherentName(adherent)}
        className="h-7 w-7 rounded-full object-cover"
        onError={(event) => {
          event.currentTarget.src = fallbackPhoto(adherent?.genre);
        }}
      />

      <div className="min-w-0">
        <p className="max-w-[180px] truncate text-xs font-bold text-slate-900 dark:text-white">
          {getAdherentName(adherent)}
        </p>
      </div>

      <button
        type="button"
        onClick={() => onRemove(adherent.id)}
        className="flex h-6 w-6 items-center justify-center rounded-full bg-rose-50 text-rose-700 transition hover:bg-rose-100 dark:bg-rose-500/10 dark:text-rose-300 dark:hover:bg-rose-500/20"
        title="Retirer"
      >
        <FaUserMinus size={10} />
      </button>
    </div>
  );
}

export default function ManageAdherentsDrawer({
  isOpen,
  onClose,
  userId,
  refresh,
}) {
  const inputRef = useRef(null);

  const [allAdherents, setAllAdherents] = useState([]);
  const [linkedList, setLinkedList] = useState([]);

  const [currentIds, setCurrentIds] = useState(new Set());
  const [selectedIds, setSelectedIds] = useState(new Set());

  const [query, setQuery] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen || !userId) return;

    let mounted = true;

    const fetchData = async () => {
      try {
        setLoading(true);

        const [adherentsResponse, linkedResponse] = await Promise.all([
          getAllAdherentsListWithGroup(),
          getAdherentsByUser(userId),
        ]);

        if (!mounted) return;

        const safeAll = Array.isArray(adherentsResponse)
          ? adherentsResponse
          : [];
        const safeLinked = Array.isArray(linkedResponse)
          ? linkedResponse
          : [];

        const linkedIds = new Set(
          safeLinked
            .map((adherent) => adherent?.id)
            .filter((id) => id !== null && id !== undefined)
            .map(String)
        );

        setAllAdherents(safeAll);
        setLinkedList(safeLinked);
        setCurrentIds(linkedIds);
        setSelectedIds(new Set(linkedIds));

        setQuery("");
        setDropdownOpen(false);
        setHighlight(0);
      } catch (error) {
        console.error("Erreur chargement adhérents liés :", error);
        toast.error("Erreur lors du chargement des adhérents liés.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchData();

    return () => {
      mounted = false;
    };
  }, [isOpen, userId]);

  const candidates = useMemo(() => {
    const q = query.trim().toLowerCase();

    const base = allAdherents.filter((adherent) => {
      return !selectedIds.has(String(adherent?.id));
    });

    if (!q) return base.slice(0, MAX_SUGGESTIONS);

    return base
      .filter((adherent) => {
        const name = `${adherent?.nom || ""} ${adherent?.prenom || ""}`.toLowerCase();
        const group = formatGroups(adherent).toLowerCase();
        return name.includes(q) || group.includes(q);
      })
      .slice(0, MAX_SUGGESTIONS);
  }, [allAdherents, selectedIds, query]);

  const selectedAdherents = useMemo(() => {
    const mapAll = new Map(
      allAdherents.map((adherent) => [String(adherent.id), adherent])
    );

    return [...selectedIds]
      .map((id) => {
        return (
          mapAll.get(id) ||
          linkedList.find((adherent) => String(adherent?.id) === String(id))
        );
      })
      .filter(Boolean)
      .sort((a, b) => {
        return getAdherentName(a).localeCompare(getAdherentName(b), "fr");
      });
  }, [allAdherents, linkedList, selectedIds]);

  const toAdd = useMemo(() => {
    return [...selectedIds].filter((id) => !currentIds.has(id));
  }, [selectedIds, currentIds]);

  const toRemove = useMemo(() => {
    return [...currentIds].filter((id) => !selectedIds.has(id));
  }, [selectedIds, currentIds]);

  const addAdherent = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.add(String(id));
      return next;
    });

    setQuery("");
    setDropdownOpen(true);
    setHighlight(0);
    inputRef.current?.focus();
  };

  const removeAdherent = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(String(id));
      return next;
    });
  };

  const handleKeyDown = (event) => {
    if (!dropdownOpen || candidates.length === 0) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlight((value) => Math.min(value + 1, candidates.length - 1));
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlight((value) => Math.max(value - 1, 0));
    }

    if (event.key === "Enter") {
      event.preventDefault();
      const selected = candidates[highlight];
      if (selected) addAdherent(selected.id);
    }

    if (event.key === "Escape") {
      setDropdownOpen(false);
    }
  };

  const handleSubmit = async (event) => {
    event?.preventDefault?.();

    if (submitting) return;

    try {
      setSubmitting(true);

      await Promise.all([
        ...toAdd.map((id) => assignAdherentToUser(userId, Number(id))),
        ...toRemove.map((id) => removeAdherentFromUser(userId, Number(id))),
      ]);

      toast.success(
        `Liens mis à jour : ${toAdd.length} ajout(s), ${toRemove.length} retrait(s).`
      );

      await refresh?.();
      onClose?.();
    } catch (error) {
      console.error("Erreur mise à jour adhérents utilisateur :", error);
      toast.error("Erreur lors de l'enregistrement des liaisons.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <UserDrawer
      isOpen={isOpen}
      onClose={onClose}
      title="Gérer les adhérents liés"
      subtitle="Associez un ou plusieurs adhérents à ce compte utilisateur."
      width="max-w-4xl"
    >
      <form onSubmit={handleSubmit} className="flex min-h-full flex-col">
        <div className="flex-1 space-y-5 p-4 sm:p-6">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-2xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-indigo-700 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] opacity-75">
                Liés
              </p>
              <p className="mt-1 text-2xl font-bold text-slate-950 dark:text-white">
                {selectedIds.size}
              </p>
            </div>

            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] opacity-75">
                À ajouter
              </p>
              <p className="mt-1 text-2xl font-bold text-slate-950 dark:text-white">
                {toAdd.length}
              </p>
            </div>

            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] opacity-75">
                À retirer
              </p>
              <p className="mt-1 text-2xl font-bold text-slate-950 dark:text-white">
                {toRemove.length}
              </p>
            </div>
          </div>

          <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/60">
            <div className="mb-3 flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
                <FaSearch />
              </span>

              <div>
                <h3 className="font-bold text-slate-900 dark:text-white">
                  Ajouter un adhérent
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  Recherchez par nom, prénom ou groupe.
                </p>
              </div>
            </div>

            <div className="relative">
              <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 focus-within:ring-2 focus-within:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950">
                <FaSearch className="text-slate-400" />

                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(event) => {
                    setQuery(event.target.value);
                    setDropdownOpen(true);
                    setHighlight(0);
                  }}
                  onFocus={() => setDropdownOpen(true)}
                  onKeyDown={handleKeyDown}
                  placeholder="Nom, prénom ou groupe..."
                  className="w-full bg-transparent text-sm text-slate-900 outline-none dark:text-white"
                />
              </div>

              {dropdownOpen ? (
                <div
                  className="absolute z-20 mt-2 max-h-80 w-full overflow-auto rounded-[20px] border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-950"
                  onMouseDown={(event) => event.preventDefault()}
                >
                  {loading ? (
                    <div className="p-4 text-sm text-slate-500 dark:text-slate-400">
                      Chargement...
                    </div>
                  ) : candidates.length === 0 ? (
                    <div className="p-4 text-sm text-slate-500 dark:text-slate-400">
                      Aucun adhérent disponible.
                    </div>
                  ) : (
                    candidates.map((adherent, index) => (
                      <AdherentSuggestionRow
                        key={adherent.id}
                        adherent={adherent}
                        active={index === highlight}
                        onAdd={addAdherent}
                      />
                    ))
                  )}
                </div>
              ) : null}
            </div>
          </div>

          <div className="rounded-[24px] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/60">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
                  <FaUsers />
                </span>

                <div>
                  <h3 className="font-bold text-slate-900 dark:text-white">
                    Adhérents liés
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    Cliquez sur le bouton rouge pour retirer une liaison.
                  </p>
                </div>
              </div>

              <span className="rounded-2xl bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                {selectedAdherents.length}
              </span>
            </div>

            {selectedAdherents.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center dark:border-slate-700 dark:bg-slate-900">
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  Aucun adhérent lié à ce compte.
                </p>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {selectedAdherents.map((adherent) => (
                  <SelectedAdherentPill
                    key={adherent.id}
                    adherent={adherent}
                    onRemove={removeAdherent}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/60">
            <h3 className="mb-3 flex items-center gap-2 font-bold text-slate-900 dark:text-white">
              <FaCheck className="text-emerald-500" />
              Vue détaillée des adhérents sélectionnés
            </h3>

            <div className="max-h-[360px] overflow-auto rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950">
              <table className="min-w-[720px] w-full text-sm">
                <thead className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-900">
                  <tr className="text-left">
                    <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                      Adhérent
                    </th>
                    <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                      Groupe
                    </th>
                    <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                      Statut
                    </th>
                    <th className="px-4 py-3 text-right font-bold text-slate-700 dark:text-slate-200">
                      Action
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {selectedAdherents.length === 0 ? (
                    <tr>
                      <td
                        colSpan={4}
                        className="px-4 py-8 text-center text-slate-500 dark:text-slate-400"
                      >
                        Aucun adhérent sélectionné.
                      </td>
                    </tr>
                  ) : (
                    selectedAdherents.map((adherent) => (
                      <tr
                        key={adherent.id}
                        className="transition hover:bg-slate-50 dark:hover:bg-slate-900"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <img
                              src={getAdherentPhoto(adherent)}
                              alt={getAdherentName(adherent)}
                              className="h-10 w-10 rounded-2xl object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                              onError={(event) => {
                                event.currentTarget.src = fallbackPhoto(
                                  adherent?.genre
                                );
                              }}
                            />

                            <div className="min-w-0">
                              <p className="font-bold text-slate-900 dark:text-white">
                                {getAdherentName(adherent)}
                              </p>
                              <p className="text-xs text-slate-500 dark:text-slate-400">
                                ID {adherent.id}
                              </p>
                            </div>
                          </div>
                        </td>

                        <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                          {formatGroups(adherent)}
                        </td>

                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ring-1 ${getAdherentStatusClasses(
                              adherent?.statut_inscription
                            )}`}
                          >
                            {adherent?.statut_inscription || "Non renseigné"}
                          </span>
                        </td>

                        <td className="px-4 py-3 text-right">
                          <button
                            type="button"
                            onClick={() => removeAdherent(adherent.id)}
                            className="inline-flex items-center gap-2 rounded-2xl bg-rose-600 px-3 py-2 text-xs font-bold text-white transition hover:bg-rose-700"
                          >
                            <FaUserMinus />
                            Retirer
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 p-4 dark:border-slate-800 sm:px-6">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaTimes />
            Annuler
          </button>

          <button
            type="submit"
            disabled={submitting || loading}
            className="inline-flex items-center gap-2 rounded-2xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FaSave />
            {submitting ? "Enregistrement..." : "Enregistrer"}
          </button>
        </div>
      </form>
    </UserDrawer>
  );
}